import { FormContext, ValidateFunction } from "common/components/Form/FormContext";
import React, { ReactNode, useEffect, useRef, useState } from "react";
import { Map } from "immutable";
import { useDispatch, useSelector } from "react-redux";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { parsePath } from "common/helpers/FormPath";

export type Nullable<T> = T | null | undefined;

export interface FormProps {
  rootPath: (string | number)[];
  updateFormReducer?: any;
  onSubmit: (event: any) => void;
  validate?: (args: any) => { name: string; error?: Nullable<string> }[];
  formProps?: Partial<React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>>;
  validateOnMount?: boolean;
  children: ReactNode;
  scrollOnError?: boolean;
  editContext?: any;
}

export const Form: React.FC<FormProps> = ({
  rootPath,
  updateFormReducer = RsfFormActions.update,
  onSubmit,
  children,
  validate,
  formProps,
  validateOnMount,
  scrollOnError = true,
  editContext,
}) => {
  const dispatch = useDispatch();
  const validationSubscriptions = useRef(Map<string, ValidateFunction>());
  const errors: Map<string, string> =
    useSelector((state: any) => state.getIn(["rsfForms", "errors", ...rootPath])) || Map<string, string>();
  const [validateArgs, setValidateArgs] = useState(null);
  const [, setForceUpdate] = useState(0);
  const rootRecord = useSelector((state: any) => state.getIn(rootPath));
  const updateForm = (args: any) => {
    setValidateArgs(args);

    return updateFormReducer({ ...args, editContext });
  };

  const runValidation = (args: any, isSubmitting = false) => {
    let newErrors = errors;
    if (validate) {
      const validationErrors = validate(args);

      validationErrors.forEach(({ name, error }) => {
        newErrors = error ? newErrors.set(name, error) : newErrors.delete(name);
      });
    }
    if (validationSubscriptions.current.size > 0) {
      validationSubscriptions.current.forEach((validate, name) => {
        // A two-argument validator derives its verdict from the whole root record, so with no record
        // there is nothing to check — skip it and leave the field's existing error alone. Scoping the
        // skip by arity (rather than early-returning from runValidation) keeps the one-argument
        // subscriptions live, since an undefined root is legitimate: `rsfForms.byForm.*` roots start
        // as an empty Map. Without this, a teardown that clears the root path mid-unmount hands
        // undefined to every two-argument validator and the resulting TypeError escapes a passive
        // effect, tearing down the tree into the root errorElement.
        if (validate.length === 2 && !rootRecord) return;

        if (validate.length === 2 || name === args.name || isSubmitting || args.name === undefined) {
          let error = undefined;

          if (isSubmitting || args.name === undefined) {
            const value = rootRecord?.getIn(parsePath(name));
            error = validate(value, rootRecord);
            if (error && scrollOnError) {
              const field = document.querySelector<HTMLInputElement>(`[name="${name}"]`);
              if (field) {
                if (field.scrollIntoView) field.scrollIntoView({ behavior: "smooth", block: "center" });
                field.focus();
              }
            }
          } else {
            const value = args.name === name ? args.value : rootRecord?.getIn(parsePath(name));
            error = validate(value, rootRecord?.setIn(parsePath(args.name), args.value));
          }
          newErrors = error ? newErrors.set(name, error) : newErrors.delete(name);
        }
      });
    }

    // Only update if errors changed
    if (!errors.equals(newErrors)) {
      dispatch(RsfFormActions.updateErrors({ rootPath, errors: newErrors }));
    }
    setForceUpdate((prevState) => prevState + 1);

    return newErrors;
  };

  // handles form submission
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();

    let newErrors = runValidation({ rootPath }, true);

    newErrors.forEach((error, name) => {
      // remove manually added errors that are not related to a validation subscription
      if (!validationSubscriptions.current.get(name)) {
        newErrors = newErrors.delete(name);
      }
    });

    if (Object.keys(newErrors.toJS()).length > 0) {
      return;
    }
    onSubmit(event);
  };
  const addValidate = (name: string, validate: ValidateFunction) => {
    validationSubscriptions.current = validationSubscriptions.current.set(name, validate);
  };

  const removeValidate = (name: string) => {
    dispatch(RsfFormActions.updateErrors({ rootPath, errors: errors.delete(name) }));
    validationSubscriptions.current = validationSubscriptions.current.delete(name);
  };

  const formOnChange = (args: any) => {
    if (!validateOnMount) {
      const target = args.target;
      // A checkbox's `.value` is its value attribute (e.g. "on"), not its checked state — read `.checked` so
      // the live-validation patch reflects the toggle instead of running one change behind.
      const value = target.type === "checkbox" ? target.checked : target.value;
      runValidation({ rootPath, name: target.name, value });
      setValidateArgs(null);
    }
  };

  useEffect(() => {
    if (validateOnMount) {
      runValidation(validateArgs || { rootPath });
      setValidateArgs(null);
    }
  }, [rootRecord]);

  useEffect(() => {
    dispatch(RsfFormActions.updateErrors({ rootPath, errors: Map() }));
    setForceUpdate((prevState) => prevState + 1);
  }, [JSON.stringify(rootPath)]);

  return (
    <FormContext.Provider
      value={{
        errors,
        rootPath,
        updateFormReducer: updateForm,
        validationSubscriptions: validationSubscriptions.current,
        addValidate,
        removeValidate,
      }}>
      <form onSubmit={handleSubmit} onChange={formOnChange} role="form" {...formProps}>
        {children}
      </form>
    </FormContext.Provider>
  );
};
